/* The care-plan questionnaire atoms that cannot be placed into the answer as it
 * now stands, published for the editor's review bar.
 *
 * An atom like that gets no card in the questionnaire. It is listed instead in
 * the same couldn't-be-placed dialog the note's own suggestions use, so the
 * clinician sees it and dismisses it rather than the AI's work vanishing — and
 * the record stays pending until they do, exactly as a narrative record does.
 *
 * Only the mounted questionnaire can say which atoms these are (it holds the
 * live answer), while the bar belongs to the editor around it, so the list is
 * handed across here. Each row is computed live: it disappears on its own once
 * the condition clears — the line is blank again, the duplicate was decided —
 * and the atom's card comes back.
 *
 * Two surfaces can render the same questionnaire. Each publishes under its own
 * owner, and the most recently mounted one is the list that is read, the same
 * "later mount owns the slot" rule the decision executors follow. Keeping every
 * owner's publication rather than one slot means an earlier surface's list is
 * still there to fall back to when the later one unmounts. */

import { useMemo } from 'react'
import { create } from 'zustand'
import type { DroppedSuggestion } from '#src/hooks/useAISuggestionHydration.ts'

/* One row of the dialog, plus what a dismissal needs to reach the atom. `id`
 * is a composite of the staging record and the atom (see
 * `droppedQuestionnaireAtomId`), because the dialog keys and dismisses rows by
 * id and one record holds many atoms. */
export interface DroppedQuestionnaireAtom extends DroppedSuggestion {
  questionnaireUrl: string
  recordId: string
  atomKey: string
}

// Identity token only: publications are matched by reference, never by content.
export interface PublicationOwner {
  readonly [key: string]: never
}

interface Publication {
  owner: PublicationOwner
  rows: readonly DroppedQuestionnaireAtom[]
  // The publishing questionnaire's own reject, so a dismissal is decided by the
  // same state machine as a card's Reject.
  dismiss: (atomKey: string) => void
}

interface DroppedQuestionnaireAtomsState {
  // Publications per questionnaire url, earliest mount first.
  byUrl: Readonly<Record<string, readonly Publication[]>>
}

export const useDroppedQuestionnaireAtomsStore =
  create<DroppedQuestionnaireAtomsState>(() => ({ byUrl: {} }))

/* Prefixed so a composite can never equal a staging record id, which the
 * dialog's other rows carry; the record id comes first and never holds `#`,
 * so the first `#` after the prefix splits it from the atom key (which may). */
const ID_PREFIX = 'questionnaire-atom:'

export function droppedQuestionnaireAtomId(
  recordId: string,
  atomKey: string
): string {
  return `${ID_PREFIX}${recordId}#${atomKey}`
}

export function parseDroppedQuestionnaireAtomId(
  id: string
): { recordId: string; atomKey: string } | null {
  if (!id.startsWith(ID_PREFIX)) {
    return null
  }
  const rest = id.slice(ID_PREFIX.length)
  const split = rest.indexOf('#')
  if (split <= 0 || split === rest.length - 1) {
    return null
  }
  return { recordId: rest.slice(0, split), atomKey: rest.slice(split + 1) }
}

export function publishDroppedQuestionnaireAtoms(
  questionnaireUrl: string,
  owner: PublicationOwner,
  rows: readonly DroppedQuestionnaireAtom[],
  dismiss: (atomKey: string) => void
): void {
  useDroppedQuestionnaireAtomsStore.setState((state) => {
    const publications = state.byUrl[questionnaireUrl] ?? []
    const publication: Publication = { owner, rows, dismiss }
    const index = publications.findIndex((p) => p.owner === owner)
    // A republish keeps its place, so only mounting decides who owns the slot.
    const next =
      index === -1
        ? [...publications, publication]
        : publications.map((p, i) => (i === index ? publication : p))
    return { byUrl: { ...state.byUrl, [questionnaireUrl]: next } }
  })
}

export function withdrawDroppedQuestionnaireAtoms(
  questionnaireUrl: string,
  owner: PublicationOwner
): void {
  useDroppedQuestionnaireAtomsStore.setState((state) => {
    const publications = state.byUrl[questionnaireUrl]
    if (!publications?.some((p) => p.owner === owner)) {
      return state
    }
    const next = publications.filter((p) => p.owner !== owner)
    if (next.length > 0) {
      return { byUrl: { ...state.byUrl, [questionnaireUrl]: next } }
    }
    const { [questionnaireUrl]: _withdrawn, ...byUrl } = state.byUrl
    return { byUrl }
  })
}

function activePublications(
  state: DroppedQuestionnaireAtomsState
): Publication[] {
  return Object.values(state.byUrl).flatMap((publications) => {
    const active = publications.at(-1)
    return active ? [active] : []
  })
}

// Every questionnaire's current list, flattened.
export function useDroppedQuestionnaireAtoms(): readonly DroppedQuestionnaireAtom[] {
  const byUrl = useDroppedQuestionnaireAtomsStore((state) => state.byUrl)
  return useMemo(
    () => activePublications({ byUrl }).flatMap((p) => p.rows),
    [byUrl]
  )
}

/* Reject the atom a row stands for, through the questionnaire that listed it.
 * False when no current list holds that row — it was decided, or became
 * placeable, between the render and the click — so the caller does nothing
 * rather than dismissing something else under its id. */
export function dismissDroppedQuestionnaireAtom(id: string): boolean {
  for (const publication of activePublications(
    useDroppedQuestionnaireAtomsStore.getState()
  )) {
    const row = publication.rows.find((r) => r.id === id)
    if (row) {
      publication.dismiss(row.atomKey)
      return true
    }
  }
  return false
}
